import React from 'react'
import { useTranslation } from 'react-i18next'

import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableCell from '@mui/material/TableCell'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import CloseIcon from '@mui/icons-material/Close'

import { GraphDatapoint } from '../../types/graphSeriesConfig'
import { GraphDataParam, GraphDataParamString } from '../../types/enums/graphDataParam'
import { canDeletePoint } from '../../utils/profileEditorUtils'
import GraphPointEditor from './graphPointEditor'
import AdjustValuesModal from './adjustValuesModal'
import TooltipIconButton from '../fields/tooltipIconButton'
import { msToTime } from '../../utils/dateUtils'
import { getValueSuffix } from '../../utils/graphUtils'
import { SeriesType } from '../../types/enums/seriesType'

const NEW_POINT_INDEX = -1

interface ProfileEditorPointsProps {
  editParam: GraphDataParam
  points: Array<GraphDatapoint>
  selectedPointIndexes: Array<number>
  valueSuffix: string
  minX: number
  maxX: number
  moveIntervalX: number
  isReadonly: boolean
  isXaxisTemp: boolean
  onDeletePoint: (param: GraphDataParam, index: number) => void
  onChangePoint: (param: GraphDataParam, index: number, newTime: number, newValue: number) => void
  onCreateNewPoint: (param: GraphDataParam, newTime: number, newValue: number) => void
  onBatchUpdate: (param: GraphDataParam, pointsArray: Array<GraphDatapoint>) => void
}

const ProfileEditorPoints = (props: ProfileEditorPointsProps): JSX.Element => {
  const { t } = useTranslation()

  const [editProperty, setEditProperty] = React.useState<{ index: number; property: 'time' | 'value' } | null>(null)
  const [adjustValuesParam, setAdjustValuesParam] = React.useState<null | GraphDataParam>(null)

  React.useEffect(() => {
    setEditProperty(null)
  }, [props.editParam])

  const getAdjustNumberLabel = (): string => {
    return props.selectedPointIndexes.length === props.points.length || props.selectedPointIndexes.length < 2
      ? t('editprofileview.all')
      : `${props.selectedPointIndexes.length}`
  }

  return (
    <Stack sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 1.5, width: '120px' }}>
                {props.isXaxisTemp ? t('editprofileview.beantemp') : t('editprofileview.time')}
              </TableCell>
              <TableCell sx={{ pl: 1.5 }} colSpan={2}>
                {t(GraphDataParamString[props.editParam])}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.points.map((point, index) => {
              const isFirstPoint = index !== NEW_POINT_INDEX && index === 0
              if (editProperty && editProperty.index === index) {
                return (
                  <GraphPointEditor
                    key={`${props.editParam}-${index}`}
                    time={point.msec}
                    numValue={point.dataValue || 0}
                    isFirstPoint={isFirstPoint}
                    editProp={editProperty.property}
                    valueSuffix={props.valueSuffix}
                    editParam={props.editParam}
                    minX={props.minX}
                    maxX={props.maxX}
                    isXaxisTemp={props.isXaxisTemp}
                    onSubmit={(newTime: number, newValue: number) => {
                      props.onChangePoint(props.editParam, index, newTime, newValue)
                      setEditProperty(null)
                    }}
                    onCancel={() => setEditProperty(null)}
                  />
                )
              }
              return (
                <TableRow
                  key={`${props.editParam}-${index}`}
                  sx={{ backgroundColor: props.selectedPointIndexes.indexOf(index) > -1 ? 'secondary.light' : 'none' }}
                >
                  <TableCell sx={{ p: 0 }}>
                    <Button
                      onClick={(): void => setEditProperty({ index: index, property: 'time' })}
                      disabled={props.isReadonly || isFirstPoint}
                    >
                      {props.isXaxisTemp && isFirstPoint && t('editprofileview.start')}
                      {props.isXaxisTemp && !isFirstPoint && `${point.msec}${getValueSuffix(SeriesType.TEMPERATURE)}`}
                      {!props.isXaxisTemp && msToTime(point.msec)}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ p: 0 }}>
                    <Button
                      onClick={(): void => setEditProperty({ index: index, property: 'value' })}
                      disabled={props.isReadonly}
                    >{`${point.dataValue}${props.valueSuffix}`}</Button>
                  </TableCell>
                  <TableCell sx={{ p: 0 }}>
                    {canDeletePoint(props.editParam, index, props.points.length) && (
                      <TooltipIconButton
                        onClick={() => props.onDeletePoint(props.editParam, index)}
                        tooltip={t('editprofileview.deletepoint')}
                        icon={<CloseIcon />}
                        disabled={props.isReadonly}
                        color="primary"
                      />
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {editProperty && editProperty.index === NEW_POINT_INDEX && (
              <GraphPointEditor
                time={0}
                numValue={0}
                isFirstPoint={props.points.length === 0}
                editProp={editProperty.property}
                valueSuffix={props.valueSuffix}
                editParam={props.editParam}
                minX={props.minX}
                maxX={props.maxX}
                isXaxisTemp={props.isXaxisTemp}
                onSubmit={(newTime: number, newValue: number) => {
                  props.onCreateNewPoint(props.editParam, newTime, newValue)
                  setEditProperty(null)
                }}
                onCancel={() => setEditProperty(null)}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between">
        {editProperty?.index !== NEW_POINT_INDEX && (
          <Button
            variant="outlined"
            disabled={props.isReadonly}
            onClick={() =>
              setEditProperty({ index: NEW_POINT_INDEX, property: props.points.length === 0 ? 'value' : 'time' })
            }
          >
            {t('editprofileview.addpoint')}
          </Button>
        )}
        <Button disabled={props.isReadonly} onClick={() => setAdjustValuesParam(props.editParam)}>
          {t('editprofileview.adjustvalues').replace('{0}', getAdjustNumberLabel())}
        </Button>
      </Stack>
      {!!adjustValuesParam && (
        <AdjustValuesModal
          isXaxisTemp={props.isXaxisTemp}
          editParam={props.editParam}
          pointsArray={props.points}
          valueSuffix={props.valueSuffix}
          maxX={props.maxX}
          moveIntervalX={props.moveIntervalX}
          adjustNumberLabel={getAdjustNumberLabel()}
          selectedPointIndexes={
            props.selectedPointIndexes.length > 1 ? props.selectedPointIndexes : props.points.map((_x, index) => index)
          }
          onClose={() => setAdjustValuesParam(null)}
          onDispatch={(pointsArray: Array<GraphDatapoint>) => {
            setAdjustValuesParam(null)
            props.onBatchUpdate(props.editParam, pointsArray)
          }}
        />
      )}
    </Stack>
  )
}

export default ProfileEditorPoints
